import React, { useEffect, useState } from 'react';
import {
	Card,
	CardContent,
	Collapse,
	Typography,
	Chip,
	Box,
	Grid,
	Link,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	IconButton,
} from '@mui/material';
import { HelpOutline } from '@mui/icons-material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { getFlightAndAncillaryIssueSummariesList } from '../../../../../features/operations/services';
import { getTransactionDetails } from '../../../../../features/transactions/services';
import { ITransactionDetails } from '../../../../../features/transactions/types';
import { formatDateToShowUser } from '../../../../../features/common/helpers';
import { buildTransactionDetailsPath } from 'features/transaction-details/helpers/transactionDetailsNavigation';
import SectionCard from './SectionCard';

/* eslint-disable react/prop-types */

interface DetalleCobroAerolineasProps {
	saleOrderId: string;
}

interface CardToAirlinePaymentLine {
	cardToAirlineOrderId: string;
	status: string;
	amount: number;
	createdAt: string;
	updatedAt?: string;
	caseIdCompleteLink?: number;
	transactionId?: number;
	cardStamp?: string;
	fee?: number;
}

/**
 * Cache a nivel de módulo: el componente vive solo en el layout desktop, así
 * que cruzar el breakpoint lo desmonta/remonta — sin cache, cada resize
 * re-disparaba los requests. Dura lo que dura la sesión de la SPA.
 */
const paymentLinesCache = new Map<string, CardToAirlinePaymentLine[]>();
const transactionDetailsCache = new Map<number, ITransactionDetails>();

const DetalleCobroAerolineas: React.FC<DetalleCobroAerolineasProps> = ({ saleOrderId }) => {
	const [cardToAirlinePaymentLines, setCardToAirlinePaymentLines] = useState<CardToAirlinePaymentLine[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [transactionDetails, setTransactionDetails] = useState<Record<number, ITransactionDetails>>({});
	const [helpDialogOpen, setHelpDialogOpen] = useState(false);
	/** Detalle colapsado por defecto: con varias autorizaciones la card se hacía interminable. */
	const [expandedLines, setExpandedLines] = useState<Set<number>>(new Set());

	const toggleLine = (index: number): void => {
		setExpandedLines((prev) => {
			const next = new Set(prev);
			next.has(index) ? next.delete(index) : next.add(index);
			return next;
		});
	};
	const [selectedTransactionHelp, setSelectedTransactionHelp] = useState<{
		paymentLine: CardToAirlinePaymentLine;
		transactionDetail: ITransactionDetails;
	} | null>(null);

	const handleHelpClick = (paymentLine: CardToAirlinePaymentLine, transactionDetail: ITransactionDetails) => {
		setSelectedTransactionHelp({ paymentLine, transactionDetail });
		setHelpDialogOpen(true);
	};

	const handleHelpClose = () => {
		setHelpDialogOpen(false);
		setSelectedTransactionHelp(null);
	};

	const fetchTransactionDetails = async (transactionId: number) => {
		const cached = transactionDetailsCache.get(transactionId);
		if (cached) {
			setTransactionDetails((prev) => ({ ...prev, [transactionId]: cached }));
			return;
		}

		try {
			const response = await getTransactionDetails(transactionId);
			transactionDetailsCache.set(transactionId, response.data);
			setTransactionDetails((prev) => ({
				...prev,
				[transactionId]: response.data,
			}));
		} catch (err) {
			console.error(`Error fetching transaction details for ID ${transactionId}:`, err);
			// No hacer nada si falla, mantener la funcionalidad existente
		}
	};

	useEffect(() => {
		const applyPaymentLines = (paymentLines: CardToAirlinePaymentLine[]) => {
			setCardToAirlinePaymentLines(paymentLines);

			// Obtener detalles de transacciones completadas
			paymentLines
				.filter((line) => line.status === 'completed' && line.transactionId)
				.forEach((line) => {
					if (line.transactionId) {
						fetchTransactionDetails(line.transactionId);
					}
				});
		};

		const fetchCardToAirlinePaymentData = async () => {
			const cachedLines = paymentLinesCache.get(saleOrderId);
			if (cachedLines) {
				applyPaymentLines(cachedLines);
				setLoading(false);
				return;
			}

			try {
				setLoading(true);
				setError(null);

				const response = await getFlightAndAncillaryIssueSummariesList(
					1, // page
					15, // size
					[], // sortingFields
					{ saleOrderId }, // filters
				);

				if (response.data && response.data.elements && response.data.elements.length > 0) {
					// Tomar solo el primer elemento como se solicita
					const firstElement = response.data.elements[0];
					const paymentLines = firstElement.cardToAirlinePaymentLinkSummaryLines || [];
					paymentLinesCache.set(saleOrderId, paymentLines);
					applyPaymentLines(paymentLines);
				} else {
					paymentLinesCache.set(saleOrderId, []);
					setCardToAirlinePaymentLines([]);
				}
			} catch (err) {
				console.error('Error fetching card to airline payment data:', err);
				setError('Error al cargar los datos de cobros contra aerolíneas');
				setCardToAirlinePaymentLines([]);
			} finally {
				setLoading(false);
			}
		};

		if (saleOrderId) {
			fetchCardToAirlinePaymentData();
		}
	}, [saleOrderId]);

	return (
		<>
			<SectionCard title="Detalle de cobros contra aerolíneas" icon={<CreditCardIcon fontSize="small" />}>
				{loading && (
					<Typography variant="body2" color="text.secondary">
						Cargando datos de cobros...
					</Typography>
				)}

				{error && (
					<Typography variant="body2" color="error">
						{error}
					</Typography>
				)}

				{!loading && !error && (
					<>
						{cardToAirlinePaymentLines.length > 0 ? (
							<Grid container spacing={2}>
								{cardToAirlinePaymentLines.map((paymentLine, index) => {
									const getStatusChip = (status: string) => {
										if (status === 'sent') {
											return <Chip label="Enviado al cliente" color="warning" size="small" />;
										} else if (status === 'completed') {
											return <Chip label="Completado x cliente" color="success" size="small" />;
										}
										return <Chip label={status} color="default" size="small" />;
									};

									const formatDate = (dateString: string) => {
										return new Date(dateString).toLocaleDateString('es-ES', {
											day: '2-digit',
											month: '2-digit',
											year: 'numeric',
											hour: '2-digit',
											minute: '2-digit',
										});
									};

									// Obtener detalles de transacción si está disponible
									const transactionDetail = paymentLine.transactionId
										? transactionDetails[paymentLine.transactionId]
										: null;

									const isExpanded = expandedLines.has(index);
									const collapsedSummaryParts = [
										formatDate(paymentLine.createdAt),
										paymentLine.cardStamp ? paymentLine.cardStamp.toUpperCase() : null,
										paymentLine.fee ? `${paymentLine.fee} cuotas` : null,
									].filter(Boolean);

									return (
										<Grid item xs={12} key={index}>
											<Card variant="outlined">
												<CardContent>
													<Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
														<Box>
															{/* Comparar montos y mostrar tachado si son diferentes */}
															{transactionDetail && transactionDetail.transactionData.amount !== paymentLine.amount ? (
																<>
																	<Box display="flex" alignItems="center" gap={1}>
																		<Typography
																			variant="h6"
																			color="text.secondary"
																			sx={{ textDecoration: 'line-through' }}
																		>
																			USD {paymentLine.amount}
																		</Typography>
																		<IconButton
																			size="small"
																			onClick={() => handleHelpClick(paymentLine, transactionDetail)}
																			sx={{ p: 0.5 }}
																		>
																			<HelpOutline fontSize="small" color="action" />
																		</IconButton>
																	</Box>
																	<Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
																		USD {transactionDetail.transactionData.amount}
																	</Typography>
																</>
															) : (
																<Typography variant="h6" color="primary">
																	USD {paymentLine.amount}
																</Typography>
															)}
														</Box>
														<Box display="flex" alignItems="center" gap={0.5}>
															{getStatusChip(paymentLine.status)}
															<IconButton size="small" onClick={() => toggleLine(index)}>
																<ExpandMoreIcon
																	fontSize="small"
																	sx={{
																		transform: isExpanded ? 'rotate(180deg)' : 'none',
																		transition: 'transform 0.2s',
																	}}
																/>
															</IconButton>
														</Box>
													</Box>

													<Typography variant="caption" color="text.secondary">
														{collapsedSummaryParts.join(' · ')}
													</Typography>

													<Collapse in={isExpanded}>
														<Typography variant="body2" color="text.secondary" gutterBottom>
															Creado: {formatDate(paymentLine.createdAt)}
														</Typography>

														{paymentLine.updatedAt && (
															<Typography variant="body2" color="text.secondary" gutterBottom>
																Actualizado: {formatDate(paymentLine.updatedAt)}
															</Typography>
														)}

														{paymentLine.fee && (
															<Typography variant="body2" color="text.secondary" gutterBottom>
																Cuotas: {paymentLine.fee}
															</Typography>
														)}

														{paymentLine.cardStamp && (
															<Typography variant="body2" color="text.secondary" gutterBottom>
																Tarjeta: {paymentLine.cardStamp.toUpperCase()}
															</Typography>
														)}

														{paymentLine.transactionId && (
															<Typography variant="body2" color="text.secondary" gutterBottom>
																ID Transacción:{' '}
																<Link
																	href={buildTransactionDetailsPath(paymentLine.transactionId)}
																	target="_blank"
																	rel="noopener noreferrer"
																	color="primary"
																	underline="hover"
																>
																	{paymentLine.transactionId}
																</Link>
															</Typography>
														)}

														{/* Información adicional para transacciones completadas */}
														{transactionDetail && (
															<>
																<Typography variant="body2" color="text.secondary" gutterBottom>
																	Titular: {transactionDetail.transactionData.cardholder}
																</Typography>
																<Typography variant="body2" color="text.secondary" gutterBottom>
																	Tarjeta: {transactionDetail.transactionData.cardNumberFormatted}
																</Typography>
															</>
														)}

														{/* Chip de estado de autorización */}
														{transactionDetail && (
															<Box mt={2}>
																{transactionDetail.authorizationsData.length === 0 ? (
																	<Chip
																		label="Pendiente a autorizar"
																		sx={{ backgroundColor: '#ff9800', color: 'white' }}
																		size="small"
																	/>
																) : // Si hay autorizaciones, evaluar pendingAuthorization
																transactionDetail.transactionData.pendingAuthorization === 0 ? (
																	<Chip label="Tarjeta autorizada" color="success" size="small" />
																) : (
																	<Chip
																		label={`Autorización parcial, pend: USD ${transactionDetail.transactionData.pendingAuthorization}`}
																		sx={{ backgroundColor: '#ff9800', color: 'white' }}
																		size="small"
																	/>
																)}
															</Box>
														)}

														{/* Cards de autorizaciones */}
														{transactionDetail && transactionDetail.authorizationsData.length > 0 && (
															<Box mt={2}>
																{transactionDetail.authorizationsData.map((authorization, authIndex) => {
																	const formatRefundDate = (dateString: string) => {
																		return new Date(dateString).toLocaleDateString('es-ES', {
																			day: '2-digit',
																			month: '2-digit',
																			year: 'numeric',
																			hour: '2-digit',
																			minute: '2-digit',
																		});
																	};

																	// Calcular monto real después de refunds
																	const totalRefunded = authorization.refunded.reduce(
																		(sum, refund) => sum + refund.amountRefunded,
																		0,
																	);
																	const realAmount = authorization.amountAuthorized - totalRefunded;
																	const hasRefunds = authorization.refunded.length > 0;

																	return (
																		<Card key={authIndex} variant="outlined" sx={{ mb: 1, backgroundColor: '#f5f5f5' }}>
																			<CardContent sx={{ py: 1 }}>
																				{/* Mostrar monto original tachado y monto real si hay refunds */}
																				{hasRefunds && (
																					<Box display="flex" alignItems="center" gap={1} mb={1}>
																						<Typography
																							variant="body2"
																							color="text.secondary"
																							sx={{ textDecoration: 'line-through' }}
																						>
																							<strong>USD {authorization.amountAuthorized}</strong>
																						</Typography>
																						<Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>
																							<strong>USD {realAmount}</strong>
																						</Typography>
																					</Box>
																				)}

																				<Typography variant="body2" color="text.secondary">
																					<strong>
																						USD {hasRefunds ? realAmount : authorization.amountAuthorized}
																					</strong>{' '}
																					autorizados contra <strong>{authorization.chargedAgainstOptionName}</strong>{' '}
																					en <strong>{transactionDetail.transactionData.fee}</strong> cuotas el{' '}
																					<strong>{formatDateToShowUser(authorization.paymentDate)}</strong> con el
																					codigo de autorización <strong>{authorization.authorizationCode}</strong>
																					{hasRefunds && ' *'}
																				</Typography>

																				{/* Mostrar explicación de refunds */}
																				{hasRefunds && (
																					<Box mt={1}>
																						{authorization.refunded.map((refund, refundIndex) => (
																							<Typography
																								key={refundIndex}
																								variant="caption"
																								color="text.secondary"
																								sx={{ fontSize: '0.75rem', display: 'block' }}
																							>
																								* Devolución de {refund.coinCode} {refund.amountRefunded} realizada
																								sobre esta autorización el {formatRefundDate(refund.refundedAt)} por{' '}
																								{refund.refundedBy}
																							</Typography>
																						))}
																					</Box>
																				)}
																			</CardContent>
																		</Card>
																	);
																})}
															</Box>
														)}
													</Collapse>
												</CardContent>
											</Card>
										</Grid>
									);
								})}
							</Grid>
						) : (
							<Typography variant="body2" color="text.secondary">
								No hay datos de cobros contra aerolíneas disponibles
							</Typography>
						)}
					</>
				)}
			</SectionCard>

			{/* Modal de ayuda */}
			<Dialog open={helpDialogOpen} onClose={handleHelpClose} maxWidth="sm" fullWidth>
				<DialogTitle>Diferencia en montos</DialogTitle>
				<DialogContent>
					<Typography variant="body1">
						{selectedTransactionHelp ? (
							<>
								El link de pago enviado al cliente originalmente fue por un monto de{' '}
								<strong>USD {selectedTransactionHelp.paymentLine.amount}</strong>, pero hemos hecho autorizaciones para
								esta tarjeta por un monto total de{' '}
								<strong>USD {selectedTransactionHelp.transactionDetail.transactionData.amount}</strong>.
							</>
						) : (
							'No hay información disponible'
						)}
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleHelpClose} color="primary">
						Cerrar
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default DetalleCobroAerolineas;
